import { ChangeEvent } from 'react';

import {
  TeacherEvaluation,
  Ratings as RatingsConst,
  TeacherEvaluationSchema,
} from '@youscience/commonapp-service-common';

import SectionAccordion from '@components/SectionAccordion';
import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';

import { FormState } from '@constants/commonApp/types';
import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';

import { StyledFieldsContainer, StyledFieldWrapper } from '@pages/StudentDetailsPage/components/Card.styles';

import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

const fields = [
  {
    name: 'academicAchievement',
    title: 'Academic Achievement',
  },
  {
    name: 'intellectualPromise',
    title: 'Intellectual Promise',
  },
  {
    name: 'qualityOfWriting',
    title: 'Quality of Writing',
  },
  {
    name: 'creativeThought',
    title: 'Creative Thought',
  },
  {
    name: 'productiveDiscussion',
    title: 'Productive Discussion',
  },
  {
    name: 'facultyRespect',
    title: 'Faculty Respect',
  },
  {
    name: 'disciplinedHabits',
    title: 'Disciplined Habits',
  },
  {
    name: 'maturity',
    title: 'Maturity',
  },
  {
    name: 'motivation',
    title: 'Motivation',
  },
  {
    name: 'leadership',
    title: 'Leadership',
  },
  {
    name: 'integrity',
    title: 'Integrity',
  },
  {
    name: 'setbackReactions',
    title: 'Setback Reactions',
  },
  {
    name: 'concernForOthers',
    title: 'Concern for Others',
  },
  {
    name: 'selfConfidence',
    title: 'Self Confidence',
  },
  {
    name: 'initiative',
    title: 'Initiative',
  },
  {
    name: 'overall',
    title: 'Overall',
  },
] as const;

type RatingsFields = Partial<TeacherEvaluation['ratings']>;
interface RatingsProps {
  ratings?: RatingsFields;
  onFormChange: (formState: FormState<RatingsFields>) => void;
  onExpand: () => void;
  isExtended: boolean;
  isSaved: boolean;
}

const validator = TeacherEvaluationSchema.shape.writtenEvaluation;

export const Ratings = ({ ratings = {}, onFormChange, onExpand, isExtended, isSaved }: RatingsProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<RatingsFields, typeof validator>(
    ratings,
    validator,
    isSaved,
    onFormChange,
  );

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormStateWithErrors({ [field]: event.target.value });
  };

  return (
    <SectionAccordion
      title="Ratings"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
    >
      <StyledFieldsContainer>
        {fields.map((field) => (
          <StyledFieldWrapper key={field.name} data-testid={field.name}>
            <RadioButtonGroupWithClearButton
              name={field.name}
              title={field.title}
              value={formState.data?.[field.name] ?? undefined}
              options={RatingsConst}
              handleRadioChange={handleRadioChange}
              handleClear={() => setFormStateWithErrors({ [field.name]: undefined })}
              error={!!formState.errors[field.name as keyof RatingsFields]}
              helperText={formState.errors[field.name as keyof RatingsFields] ? REQUIRED_FIELD_MESSAGE : undefined}
            />
          </StyledFieldWrapper>
        ))}
      </StyledFieldsContainer>
    </SectionAccordion>
  );
};
